import { CalendarOutlined, CloseOutlined } from "@ant-design/icons"
import { Button } from "components/Button/Button"
import { BaseLayout } from "components/BaseLayout/BaseLayout"
import { Empty } from "components/Empty/Empty"
import { Image } from "components/Image/Image"
import { ZoomableImage } from "components/Image/ZoomableImage"
import { Spinner } from "components/Spinner/Spinner"
import { useElement } from "hooks/useElement"
import { useInfiniteScroll } from "hooks/useInfiniteScroll"
import { ImageIcon } from "icons/ImageIcon"
import { NoDataIcon } from "icons/NoDataIcon"
import { WeightIcon } from "icons/WeightIcon"
import { PatientDetailItem } from "interface/patientType"
import { WeightLabel } from "modules/dashboard/WeightLabel"
import { PatientSpeedDial } from "modules/patient/PatientSpeedDial"
import { useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useHotkeys } from "react-hotkeys-hook"
import { useOwnPatientData } from "services/patients"
import { useUserRole } from "services/users"
import { cn } from "styles/cn"
import { dateAsDay, formatAsTime } from "utils/date"

export function PatientLayout() {
	const containerElement = useElement("layout-content")
	const targetScroll = useRef<HTMLUListElement>(null)
	const { data: { fullName } = {} } = useUserRole()
	const {
		data: { pages: list = [] } = {},
		hasNextPage,
		fetchNextPage,
		isFetched,
		isFetching,
		isFetchingNextPage,
	} = useOwnPatientData()
	const isLoading = isFetching || isFetchingNextPage
	const [activeItem, setActiveItem] = useState<PatientDetailItem | null>()

	useInfiniteScroll({
		target: targetScroll.current,
		hasMore: hasNextPage,
		isLoading,
		onLoadMore: fetchNextPage,
	})

	useHotkeys("esc", () => setActiveItem(null))

	return (
		<BaseLayout className="lg:grid-cols-1 xl:grid-cols-1">
			<BaseLayout.Header username={fullName} visibleHelpItems={false} />

			<BaseLayout.Main className="lg:col-start-1 xl:col-start-1">
				<Spinner spinning={isLoading}>
					<div className="mx-auto flex h-full max-h-full max-w-300 flex-col gap-4 overflow-hidden lg:gap-8">
						<h2 className="font-headings text-2xl font-semibold md:text-4xl">
							Hi, <span className="text-green-60">{fullName}</span>
						</h2>

						<ul
							ref={targetScroll}
							aria-label={`${list.length} screening(s)`}
							className="flex flex-col gap-2 overflow-y-auto pr-0.5 lg:gap-5"
						>
							{list.map((item) => {
								const time = formatAsTime(item.capturedAt)
								const day = dateAsDay(item.capturedAt)
								const screeningCaptureAt = `${time}, ${day}`
								const listItemAriaLabel = `Screening captured at ${screeningCaptureAt}`
								const imageAlt = `Image captured at ${screeningCaptureAt}`
								const hasImage = !!item.imageThumbS3Key
								const handleClick = hasImage
									? () => setActiveItem(item)
									: undefined

								return (
									<li
										key={item.capturedAt}
										aria-label={listItemAriaLabel}
										className={cn(
											"grid grid-cols-2 items-center gap-2 rounded-xl border border-mint-20 bg-white p-4 md:grid-cols-3",
											hasImage && "cursor-pointer hover:border-green-60",
										)}
										onClick={handleClick}
									>
										<div className="row-span-1 flex items-end gap-2 self-start md:row-auto">
											{!hasImage && (
												<WeightIcon.Null className="size-12 text-navy-60 md:size-20" />
											)}
											{hasImage && (
												<>
													<Image
														alt={imageAlt}
														src={item.imageThumbS3Key}
														className="size-12 rounded-2xl bg-grey-30 object-cover md:size-20"
													/>
													<ImageIcon
														aria-hidden
														className="size-6 rounded bg-mint-20 p-1"
													/>
												</>
											)}
										</div>

										<div className="flex items-center justify-start gap-2">
											<div className="grid size-11 place-items-center self-start rounded-xl bg-purple-0">
												<CalendarOutlined
													aria-hidden
													className="text-2xl text-purple-80"
												/>
											</div>
											<div className="flex flex-col">
												<time className="text-lg font-semibold text-navy-100 md:text-xl">
													{time}
												</time>
												<span className="text-navy-60 md:text-lg">{day}</span>
											</div>
										</div>

										<div className="-col-end-1 flex items-center justify-start gap-2">
											<div className="grid size-11 place-items-center self-start rounded-xl bg-grey-10">
												<WeightIcon
													aria-hidden
													className="size-6 fill-grey-80"
												/>
											</div>
											<div className="flex flex-col">
												<WeightLabel
													dataPoint={item}
													className="text-lg font-semibold text-navy-100 md:text-xl"
												/>
												<span className="text-navy-60 md:text-lg">Weight</span>
											</div>
										</div>
									</li>
								)
							})}

							{list.length === 0 && isFetched && (
								<Empty
									Icon={NoDataIcon}
									title="No data"
									description="There is no data on the current page"
								/>
							)}
						</ul>
					</div>

					{activeItem &&
						containerElement &&
						createPortal(
							<div
								role="dialog"
								aria-labelledby={activeItem.id}
								className="absolute inset-0 flex flex-col gap-2 bg-white p-5"
							>
								<div className="flex justify-between gap-1">
									<h2
										id={activeItem.id}
										className="font-headings text-xl font-bold"
									>
										{`${formatAsTime(activeItem.capturedAt)} ${dateAsDay(activeItem.capturedAt)}`}
									</h2>
									<Button
										type="text"
										icon={<CloseOutlined aria-hidden />}
										aria-label="Close"
										className="text-navy-60"
										onClick={() => setActiveItem(null)}
									/>
								</div>
								<div className="flex min-h-0 grow">
									<ZoomableImage
										src={activeItem.imageWebpS3Key ?? activeItem.imageS3Key}
										className="flex h-auto grow"
										imgClassName="object-contain"
									/>
								</div>
							</div>,
							containerElement,
						)}
				</Spinner>

				<PatientSpeedDial />
			</BaseLayout.Main>
		</BaseLayout>
	)
}
